export const setGlobalRotation = (rotation: number) => {
  if (typeof window !== 'undefined') {
    window.__SCROLL_ROTATION__ = rotation;
  }
}

export const getGlobalRotation = (): number => {
  if (typeof window !== 'undefined') {
    return window.__SCROLL_ROTATION__ || 0;
  }
  return 0;
}

// --- Forced Scroll Position for Storybook Debugging ---
export const setForcedScrollValue = (value: number | null) => {
  if (typeof window !== 'undefined') {
    window.__FORCED_SCROLL_VALUE__ = value;
    
    // Dispatch a custom event that components can listen for
    const event = new CustomEvent('forced-scroll-value-change', { detail: value });
    window.dispatchEvent(event);
  }
}

export const getForcedScrollValue = (): number | null => {
  if (typeof window !== 'undefined') {
    return window.__FORCED_SCROLL_VALUE__ !== undefined ? window.__FORCED_SCROLL_VALUE__ : null;
  }
  return null;
}

export const setScrollOverrideEnabled = (enabled: boolean) => {
  if (typeof window !== 'undefined') {
    window.__SCROLL_OVERRIDE_ENABLED__ = enabled;
    // Clear forced value when disabling override
    if (!enabled) {
      setForcedScrollValue(null);
    }
  }
}

export const getScrollOverrideEnabled = (): boolean => {
  if (typeof window !== 'undefined') {
    return !!window.__SCROLL_OVERRIDE_ENABLED__;
  }
  return false;
}
// --- End Forced Scroll Position ---

// --- Custom Animation Thresholds ---
export const setCustomAnimationThresholds = (thresholds: number[] | null) => {
  if (typeof window !== 'undefined') {
    window.__CUSTOM_ANIMATION_THRESHOLDS__ = thresholds;
  }
}

export const getCustomAnimationThresholds = (): number[] | null => {
  if (typeof window !== 'undefined') {
    return window.__CUSTOM_ANIMATION_THRESHOLDS__ || null;
  }
  return null;
}

// For multi-section components, we need to store section-specific thresholds
export type SectionThresholds = {
  main: number[];
  section1: number[];
  section2: number[];
  [key: string]: number[];
};

export const setSectionThresholds = (thresholds: SectionThresholds | null) => {
  if (typeof window !== 'undefined') {
    window.__SECTION_THRESHOLDS__ = thresholds;
  }
}

export const getSectionThresholds = (): SectionThresholds | null => {
  if (typeof window !== 'undefined') {
    return window.__SECTION_THRESHOLDS__ || null;
  }
  return null;
}

// Get thresholds for a specific section
export const getSectionSpecificThresholds = (sectionName: string = 'main'): number[] | null => {
  if (typeof window !== 'undefined' && window.__SECTION_THRESHOLDS__) {
    return window.__SECTION_THRESHOLDS__[sectionName] || window.__CUSTOM_ANIMATION_THRESHOLDS__ || null;
  }
  return window.__CUSTOM_ANIMATION_THRESHOLDS__ || null;
}
// --- End Custom Animation Thresholds ---

// --- Supported Screen Resolutions ---
export const SUPPORTED_DESKTOP_SCREENS: ReadonlyArray<[number, number]> = [
  [1920, 1080],
  [1366, 768],
  [1536, 864],
  [1280, 720],
  [1440, 900],
  [2560, 1440],
  [1600, 900],
];

export const SUPPORTED_MOBILE_SCREENS: ReadonlyArray<[number, number]> = [
  [360, 800],
  [390, 844],
  [393, 873],
  [412, 915],
  [414, 715],
  [414, 896],
  [360, 780],
  [375, 812],
  [385, 854],
  [393, 852],
  [428, 926],
  [430, 932],

  [393, 660], //Iphone 14 Pro
];

// --- Calculate Desktop Range ---
const desktopWidths = SUPPORTED_DESKTOP_SCREENS.map(([w]) => w);
const desktopHeights = SUPPORTED_DESKTOP_SCREENS.map(([, h]) => h);

export const MIN_DESKTOP_WIDTH = Math.min(...desktopWidths);
export const MAX_DESKTOP_WIDTH = Math.max(...desktopWidths);
export const MIN_DESKTOP_HEIGHT = Math.min(...desktopHeights);
export const MAX_DESKTOP_HEIGHT = Math.max(...desktopHeights);
// --- End Calculate Desktop Range ---

// --- Desktop Resolution Configuration ---
// Whether to match desktop screens exactly from the list (true) or use min/max range (false)
export const DESKTOP_EXACT_MATCH = false; 
export const DESKTOP_DIMENSION_BUFFER = 10; // pixels - used with both approaches for flexibility
// Whether to check zoom levels on desktop (can be disabled to allow all zoom levels)
export const CHECK_DESKTOP_ZOOM = true;
// --- End Desktop Resolution Configuration ---

// --- Zoom Settings ---
// The threshold at which we consider the screen to be zoomed
export const ZOOM_THRESHOLD = 0.08; // 8% deviation - slightly increased for better tolerance

// Known baseline values for devicePixelRatio based on device type
// Note: Most high-DPI/Retina displays have ratios of 1.5, 2, or 3
export const KNOWN_DEVICE_PIXEL_RATIOS = [1, 1.5, 2, 3]; // Base device pixel ratios (without zoom)

// Browser zoom levels we want to support (100%, 110%, 125%, 130%, etc)
export const SUPPORTED_ZOOM_LEVELS = [1, 1.1, 1.25, 1.3];

// Helper function to determine if a display has an acceptable zoom level
export const isAcceptableZoomLevel = (pixelRatio: number | null): boolean => {
  if (pixelRatio === null) return false;
  
  // Common zoom levels we explicitly want to support with higher tolerance
  // Direct matches for 110% and 125% zoom on various displays
  if (Math.abs(pixelRatio - 1.1) <= 0.02 ||    // Standard display at 110%
      Math.abs(pixelRatio - 1.25) <= 0.02 ||   // Standard display at 125%
      Math.abs(pixelRatio - 2.2) <= 0.05 ||    // Retina at 110%
      Math.abs(pixelRatio - 2.5) <= 0.05 ||    // Retina at 125%
      Math.abs(pixelRatio - 3.3) <= 0.05 ||    // 3x display at 110%
      Math.abs(pixelRatio - 3.75) <= 0.05) {   // 3x display at 125%
    return true;
  }
  
  // Special case: For very tiny values, assume it's a bug and accept it
  if (pixelRatio < 0.5) {
    return true;
  }
  
  // Special case: For very large values, assume it's a bug and accept it
  if (pixelRatio > 5) {
    return true;
  }
  
  // Handle high-DPI/Retina displays
  // Loop through each possible base DPR and zoom combination to find a match
  for (const baseDPR of KNOWN_DEVICE_PIXEL_RATIOS) {
    for (const zoomLevel of SUPPORTED_ZOOM_LEVELS) {
      const expectedDPR = baseDPR * zoomLevel;
      const diff = Math.abs(pixelRatio - expectedDPR);
      const diffPercentage = (diff / expectedDPR) * 100;
      
      // Use a slightly higher tolerance for common zoom levels
      const isCommonZoom = zoomLevel === 1.1 || zoomLevel === 1.25;
      const zoomThreshold = isCommonZoom ? ZOOM_THRESHOLD * 1.5 : ZOOM_THRESHOLD;
      const isClose = diff <= zoomThreshold;
      
      if (isClose) {
        return true;
      }
    }
  }
  
  // Fallback check: Is it close to any reasonable value?
  // This helps with unusual device pixel ratios or browser implementations
  const closeToDPR = KNOWN_DEVICE_PIXEL_RATIOS.some(baseDPR => {
    const diff = Math.abs(pixelRatio - baseDPR);
    const isClose = diff <= ZOOM_THRESHOLD;
    if (isClose) {
      return true;
    }
    return false;
  });
  
  if (closeToDPR) return true;
  
  // Try one more check: is it close to a reasonable zoom level (even if not in our preferred list)?
  // This covers cases like 125% zoom which we want to allow
  for (const baseDPR of KNOWN_DEVICE_PIXEL_RATIOS) {
    // Check if it's within a reasonable zoom range (0.9-1.5)
    const impliedZoom = pixelRatio / baseDPR;
    if (impliedZoom >= 0.9 && impliedZoom <= 1.5) {
      // Check if it's very close to a common zoom value
      if (Math.abs(impliedZoom - 1.1) <= 0.05) { // Allow 110% zoom with higher tolerance
        return true;
      }
      if (Math.abs(impliedZoom - 1.25) <= 0.05) { // Allow 125% zoom with higher tolerance
        return true;
      }
    }
  }
  
  return false;
};
// --- End Zoom Settings ---

// --- Screen Dimensions, Zoom, and Orientation ---
export type ScreenOrientation = 'landscape' | 'portrait' | 'unknown';

export const setScreenDimensions = (width: number, height: number) => {
  if (typeof window !== 'undefined') {
    // Store directly on window object
    window.__SCREEN_WIDTH__ = width;
    window.__SCREEN_HEIGHT__ = height;
  }
}

export const getScreenDimensions = (): { width: number | null, height: number | null } => {
  if (typeof window !== 'undefined') {
    // Access directly from window object
    const width = window.__SCREEN_WIDTH__ !== undefined ? window.__SCREEN_WIDTH__ : null;
    const height = window.__SCREEN_HEIGHT__ !== undefined ? window.__SCREEN_HEIGHT__ : null;
    return { width, height };
  }
  return { width: null, height: null };
}

export const setDevicePixelRatio = (ratio: number) => {
  if (typeof window !== 'undefined') {
    // Store directly on window object
    window.__DEVICE_PIXEL_RATIO__ = ratio;
  }
}

export const getDevicePixelRatio = (): number | null => {
  if (typeof window !== 'undefined') {
    // Access directly from window object
    const ratio = window.__DEVICE_PIXEL_RATIO__ !== undefined ? window.__DEVICE_PIXEL_RATIO__ : null;
    return ratio;
  }
  return null;
}

export const setScreenOrientation = (orientation: ScreenOrientation) => {
  if (typeof window !== 'undefined') {
    // Store directly on window object
    window.__SCREEN_ORIENTATION__ = orientation;
  }
}

export const getScreenOrientation = (): ScreenOrientation => {
  if (typeof window !== 'undefined') {
    // Access directly from window object
    return window.__SCREEN_ORIENTATION__ !== undefined ? window.__SCREEN_ORIENTATION__ : 'unknown';
  }
  return 'unknown';
}

// --- Mobile Height Tolerance ---
// Allow this much variance in mobile device height to account for browser UI elements appearing/disappearing
export const MOBILE_HEIGHT_TOLERANCE = 200; // Increased pixels

// --- End Mobile Height Tolerance ---

export type ScreenStatus = {
  isSupported: boolean;
  type: 'desktop' | 'mobile' | 'unsupported';
  width: number | null;
  height: number | null;
  pixelRatio: number | null;
  orientation: ScreenOrientation;
  zoomDetected: boolean; // New field to indicate if zoom is detected
};

export const getCurrentScreenStatus = (): ScreenStatus => {
  if (typeof window === 'undefined') {
    return {
      isSupported: true, // Default to supported during SSR
      type: 'desktop',
      width: null,
      height: null,
      pixelRatio: null,
      orientation: 'unknown',
      zoomDetected: false
    };
  }
  
  const { width, height } = getScreenDimensions();
  const pixelRatio = getDevicePixelRatio();
  const orientation = getScreenOrientation(); // Orientation determined by screenDetector
  let isSupported = false;
  let type: ScreenStatus['type'] = 'unsupported';
  
  // Determine if we have an acceptable zoom level
  const acceptableZoom = pixelRatio !== null && isAcceptableZoomLevel(pixelRatio);
  const zoomDetected = pixelRatio !== null && !acceptableZoom;

  if (width !== null && height !== null) {
    // --- Calculate effective dimensions for desktop check ---
    // For zoomed browsers, we need to calculate the "effective" dimensions
    // (what they would be at 100% zoom)
    let effectiveWidth = width;
    let effectiveHeight = height;
    
    // Get the "real" screen dimensions from the window.screen API if available
    const screenWidth = window?.screen?.width || 0;
    const screenHeight = window?.screen?.height || 0;
    
    // If screen dimensions reported by browser match supported screens, prioritize those
    const useScreenDimensions = SUPPORTED_DESKTOP_SCREENS.some(([w, h]) => 
      Math.abs(w - screenWidth) <= DESKTOP_DIMENSION_BUFFER && 
      Math.abs(h - screenHeight) <= DESKTOP_DIMENSION_BUFFER
    );
    
    if (useScreenDimensions && screenWidth > 0) {
      effectiveWidth = screenWidth;
      effectiveHeight = screenHeight;
    } 
    // Otherwise, if we have a valid pixelRatio that indicates zoom, calculate effective dimensions
    else if (pixelRatio !== null && pixelRatio !== 1 && pixelRatio > 0) {
      // When zoomed in (pixelRatio > 1), the viewport dimensions are smaller than actual screen
      // For example, at 125% zoom (pixelRatio = 1.25) on a 1366x768 screen, we'd see ~1092x614
      // So we need to DIVIDE by the pixelRatio (not multiply) to get the actual screen dimensions
      // This is because browser scaling causes the viewport to be smaller while content is zoomed in
      
      effectiveWidth = Math.round(width / (pixelRatio));
      effectiveHeight = Math.round(height / (pixelRatio));
    }
    
    // First check desktop based on effective dimensions 
    let isDesktopMatch = false;
    
    if (DESKTOP_EXACT_MATCH) {
      isDesktopMatch = SUPPORTED_DESKTOP_SCREENS.some(([supportedWidth, supportedHeight]) => {
        const widthMatch = Math.abs(effectiveWidth - supportedWidth) <= DESKTOP_DIMENSION_BUFFER;
        const heightMatch = Math.abs(effectiveHeight - supportedHeight) <= DESKTOP_DIMENSION_BUFFER;
        return widthMatch && heightMatch;
      });
    } else {
      isDesktopMatch = 
        effectiveWidth >= (MIN_DESKTOP_WIDTH - DESKTOP_DIMENSION_BUFFER) &&
        effectiveWidth <= (MAX_DESKTOP_WIDTH + DESKTOP_DIMENSION_BUFFER) &&
        effectiveHeight >= (MIN_DESKTOP_HEIGHT - DESKTOP_DIMENSION_BUFFER) &&
        effectiveHeight <= (MAX_DESKTOP_HEIGHT + DESKTOP_DIMENSION_BUFFER);
    }

    // Fallback: check if the actual screen dimensions match directly (without zoom compensation)
    if (!isDesktopMatch) {
      if (DESKTOP_EXACT_MATCH) {
        isDesktopMatch = SUPPORTED_DESKTOP_SCREENS.some(([supportedWidth, supportedHeight]) => {
          const widthMatch = Math.abs(width - supportedWidth) <= DESKTOP_DIMENSION_BUFFER;
          const heightMatch = Math.abs(height - supportedHeight) <= DESKTOP_DIMENSION_BUFFER;
          return widthMatch && heightMatch;
        });
      } else {
        isDesktopMatch = 
          width >= (MIN_DESKTOP_WIDTH - DESKTOP_DIMENSION_BUFFER) &&
          width <= (MAX_DESKTOP_WIDTH + DESKTOP_DIMENSION_BUFFER) &&
          height >= (MIN_DESKTOP_HEIGHT - DESKTOP_DIMENSION_BUFFER) &&
          height <= (MAX_DESKTOP_HEIGHT + DESKTOP_DIMENSION_BUFFER);
      }
    }

    if (isDesktopMatch) {
      // For desktop, we conditionally check zoom level
      isSupported = CHECK_DESKTOP_ZOOM ? acceptableZoom : true;
      type = 'desktop';
    } else {
      // --- Mobile Check (Unchanged) ---
      let mobileMatchFound = false;
      let matchedSize: [number, number] | null = null;

      for (const [supportedWidth, supportedHeight] of SUPPORTED_MOBILE_SCREENS) {
        // Assume list dimensions are Portrait (Width < Height normally)
        
        if (orientation === 'portrait') {
          // Compare current WxH to list WxH
          const widthMatches = Math.abs(width - supportedWidth) <= DESKTOP_DIMENSION_BUFFER; // Use buffer for width consistency
          const heightWithinTolerance = Math.abs(height - supportedHeight) <= MOBILE_HEIGHT_TOLERANCE;

          if (widthMatches && heightWithinTolerance) {
            mobileMatchFound = true;
            matchedSize = [supportedWidth, supportedHeight];
            break; 
          }
        } else if (orientation === 'landscape') {
          // Compare current WxH to swapped list HxW
          const widthMatchesSupportedHeight = Math.abs(width - supportedHeight) <= DESKTOP_DIMENSION_BUFFER; // Buffer on width
          const heightMatchesSupportedWidth = Math.abs(height - supportedWidth) <= MOBILE_HEIGHT_TOLERANCE; // Height tolerance on height

          if (widthMatchesSupportedHeight && heightMatchesSupportedWidth) {
            mobileMatchFound = true;
            matchedSize = [supportedWidth, supportedHeight]; 
            break; 
          }
        }
      }

      if (mobileMatchFound) {
        // For mobile, zoom level DOES NOT affect support status - only dimensions matter
        isSupported = true;
        type = 'mobile';
      }
    }
  }

  const status = { isSupported, type, width, height, pixelRatio, orientation, zoomDetected };
  return status;
};
// --- End Screen Dimensions, Zoom, and Orientation ---

// --- Event Dispatcher ---
export const dispatchScreenStateChange = () => {
  if (typeof window !== 'undefined') {
    const event = new CustomEvent('screen-state-change');
    window.dispatchEvent(event);
  }
}
// --- End Event Dispatcher ---

// Add type declaration
declare global {
  interface Window {
    __SCROLL_ROTATION__: number;
    __FORCED_SCROLL_VALUE__?: number | null; // Optional number or null
    __SCROLL_OVERRIDE_ENABLED__?: boolean; // Optional boolean
    __CUSTOM_ANIMATION_THRESHOLDS__?: number[] | null; // Optional array of thresholds
    __SECTION_THRESHOLDS__?: SectionThresholds | null; // Optional section-specific thresholds
    __SCREEN_WIDTH__?: number; // Current screen width
    __SCREEN_HEIGHT__?: number; // Current screen height
    __DEVICE_PIXEL_RATIO__?: number; // Current device pixel ratio
    __SCREEN_ORIENTATION__?: ScreenOrientation; // Added orientation
  }
} 